/**
 * 应用根（原站根组件 / St = "Q8pW95"）。
 *
 * 首屏状态机完全对齐线上：
 *   1. 挂载后 GET /lynchpin/api/meta
 *   2. code 非 0 -> 弹 toast（页面停留在空白态，与原站一致）
 *   3. pass 为真 -> "quest"；pass 为假 -> "loading"（带 meta.progress）
 *   4. quest 内的内容区还要等 store.ready（只有 /status 成功才置位）
 *
 * 结构：
 *   #root > div.Q8pW95.quest
 *     ├─ div.questScene[data-r]        (hcKr7K + pxd_Yd / XbQQLM)
 *     │   ├─ QuestBackground           (ueMMDx)
 *     │   ├─ QuestHeader               (rsLQgK)
 *     │   └─ QuestPage                 (XUszk0)
 *     └─ LoopAudio                     (noise.mp3 静音循环)
 */
import { useEffect } from 'react';
import { AccountProvider, useAccount } from './components/Account/AccountContext';
import { LoopAudio } from './components/Audio/LoopAudio';
import { QuestBackground } from './components/Quest/QuestBackground';
import { QuestPage } from './components/Quest/QuestPage';
import { Splash } from './components/Quest/Splash';
import { getLastSource } from './lib/api';
import { actions, useQuizState } from './lib/store';
import { toast } from './lib/toast';
import { useViewportVars } from './lib/useViewport';
import { COPY } from './types';
import './styles/theme.css';
import './components/Quest/quest.css';

/** 离线时加载页停留的时长，之后自动进入答题页 */
const OFFLINE_LOADING_MS = 2600;

function Shell() {
  const { phase, metaProgress, ready, error, myResult } = useQuizState();
  const account = useAccount();
  useViewportVars();

  // 1) 挂载即请求 /meta（原站 useEffect(..., [])）
  useEffect(() => {
    void actions.boot();
  }, []);

  // 3) 进入答题页后加载剧情（原站 Et: useEffect(() => store.init())）
  useEffect(() => {
    if (phase === 'quest') void actions.init();
  }, [phase]);

  // 4) 有角色后同步 /status（原站 useEffect(() => { role && store.sync() }, [role])）
  useEffect(() => {
    if (!account.role) return;
    void actions.sync();
  }, [account.role]);

  // 5) 兜底：若仍停留在加载页（例如把 ALLOW_LOCAL_PLAYTHROUGH 关掉、
  //    或上游吞掉了 pass），加载页播完后自动进入答题页，避免卡死。
  useEffect(() => {
    if (phase !== 'loading') return;
    const t = window.setTimeout(() => {
      if (getLastSource() === 'snapshot') actions.enterQuest();
    }, OFFLINE_LOADING_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  // 6) 错误提示走 toast（与原站一致），不用整屏错误页
  useEffect(() => {
    if (!error) return;
    if (error === lastToast) return;
    lastToast = error;
    toast(error || COPY.requestError);
  }, [error]);

  // 背景色：原站 `className:a()(ht.quest, e && null!==n && ht[n.value?"r1":"r0"])`，
  // 其中 e = end、n = myResult。也就是"出结果了才按 value 换色"，否则用基色 r0。
  const resultR = myResult ? (myResult.value ? '1' : '0') : '0';

  return (
    <div className="quest">
      <div className="questScene" data-r={resultR}>
        <QuestBackground active={ready} />
        {phase === 'loading' && <Splash progress={metaProgress} />}
        {phase === 'quest' && (
          <QuestPage
            account={account}
            inContainer={typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('skland')}
          />
        )}
      </div>
      <LoopAudio enabled />
    </div>
  );
}

/** 错误只提示一次，避免 StrictMode 下重复弹窗 */
let lastToast = '';

export default function App() {
  return (
    <AccountProvider>
      <Shell />
    </AccountProvider>
  );
}
